import { useState } from "react";
import { toast } from "sonner";
import { PlugZap, RefreshCw, Trash2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, errMsg } from "@/lib/api";

export function Card({ title, desc, children }) {
  return (
    <div className="rounded-2xl border bg-white p-5 sm:p-6 mt-4 space-y-5">
      <div><h2 className="font-display text-xl font-bold">{title}</h2>{desc && <p className="text-sm text-stone-500 mt-1">{desc}</p>}</div>
      {children}
    </div>
  );
}

export function F({ label, children }) {
  return <div className="space-y-1.5"><Label className="text-xs font-semibold text-stone-600">{label}</Label>{children}</div>;
}

function Sel({ value, onChange, options, testId }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger data-testid={testId}><SelectValue /></SelectTrigger>
      <SelectContent>{options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
    </Select>
  );
}

const PFIELDS = [["name", "Denominazione"], ["tipologia", "Tipologia"], ["indirizzo", "Indirizzo"], ["comune", "Comune"], ["provincia", "Provincia"], ["cap", "CAP"],
  ["cin", "CIN (Codice Identificativo Nazionale)"], ["codice_regionale", "Codice identificativo regionale"], ["codice_dms", "Codice struttura DMS"], ["email", "Email struttura"], ["telefono", "Telefono"]];

export function PropertyTab({ prop, onSaved }) {
  const [f, setF] = useState(Object.fromEntries(PFIELDS.map(([k]) => [k, prop[k] || ""])));
  const save = async () => {
    try {
      await api.put("/settings/property", f);
      toast.success("Dati struttura salvati");
      onSaved();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <Card title="Anagrafica struttura" desc="Numero camere e posti letto sono calcolati dall'elenco camere attive.">
      <div className="grid gap-4 sm:grid-cols-2">
        {PFIELDS.map(([k, l]) => <F key={k} label={l}><Input value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`property-${k}-input`} /></F>)}
      </div>
      <Button onClick={save} data-testid="property-save-button">Salva</Button>
    </Card>
  );
}

export function AlloggiatiTab({ prop, onSaved }) {
  const a = prop.alloggiati || {};
  const [f, setF] = useState({ utente: a.utente || "", password: "", wskey: "", mode: a.mode || "mock", auto_send: !!a.auto_send, id_appartamento: a.id_appartamento || "" });
  const [res, setRes] = useState(null);
  const [apps, setApps] = useState(null);
  const loadApps = async () => {
    try {
      const { data } = await api.get("/settings/alloggiati/appartamenti");
      setApps(data);
      if (!data.length) toast.info("Nessun appartamento: la tua utenza è di tipo struttura ricettiva");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const save = async () => {
    try {
      await api.put("/settings/alloggiati", f);
      toast.success("Configurazione Alloggiati salvata");
      setF({ ...f, password: "", wskey: "" });
      onSaved();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const test = async () => {
    const { data } = await api.post("/settings/alloggiati/test");
    setRes(data);
  };
  return (
    <Card title="Credenziali Alloggiati Web" desc="Le credenziali sono cifrate sul server e non vengono mai mostrate. La WSKEY si genera dal tuo account sul portale Alloggiati Web.">
      <div className="grid gap-4 sm:grid-cols-3">
        <F label="Utente"><Input value={f.utente} onChange={(e) => setF({ ...f, utente: e.target.value })} data-testid="alloggiati-user-input" /></F>
        <F label={`Password ${a.has_password ? "(salvata, lascia vuoto per non cambiarla)" : ""}`}><Input type="password" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} data-testid="alloggiati-password-input" /></F>
        <F label={`WSKEY ${a.has_wskey ? "(salvata)" : ""}`}><Input type="password" value={f.wskey} onChange={(e) => setF({ ...f, wskey: e.target.value })} data-testid="alloggiati-wskey-input" /></F>
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <label className="flex items-center justify-between gap-3 rounded-xl border p-4">
          <span><b className="text-sm">Modalità reale</b><br /><span className="text-xs text-stone-500">Spenta = MOCK: nessun invio alla Polizia</span></span>
          <Switch checked={f.mode === "real"} onCheckedChange={(v) => setF({ ...f, mode: v ? "real" : "mock" })} data-testid="alloggiati-mode-switch" />
        </label>
        <label className="flex items-center justify-between gap-3 rounded-xl border p-4">
          <span><b className="text-sm">Invio automatico</b><br /><span className="text-xs text-stone-500">Ogni ora invia le schedine confermate</span></span>
          <Switch checked={f.auto_send} onCheckedChange={(v) => setF({ ...f, auto_send: v })} data-testid="alloggiati-autosend-switch" />
        </label>
      </div>
      <div className="rounded-xl border p-4 space-y-3" data-testid="alloggiati-apartment-section">
        <p className="text-sm"><b>Utenza "Gestione Appartamenti"</b><br /><span className="text-xs text-stone-500">Se la tua utenza è di questo tipo, indica l'appartamento: le schedine useranno i metodi dedicati. Lascia vuoto per una struttura ricettiva normale.</span></p>
        <div className="flex flex-wrap items-end gap-2">
          <F label="ID appartamento"><Input className="w-32" value={f.id_appartamento} onChange={(e) => setF({ ...f, id_appartamento: e.target.value.replace(/\D/g, "") })} data-testid="alloggiati-apartment-input" /></F>
          <Button type="button" variant="outline" onClick={loadApps} data-testid="alloggiati-load-apartments">Carica elenco dal portale</Button>
        </div>
        {apps?.map((x) => (
          <button key={x.id} type="button" onClick={() => setF({ ...f, id_appartamento: x.id })} className={`block w-full text-left rounded-lg border px-3 py-2 text-sm ${f.id_appartamento === x.id ? "border-primary bg-primary/5" : ""}`} data-testid={`alloggiati-apartment-${x.id}`}>
            <b>ID {x.id}</b> · {x.descrizione} — {x.indirizzo}, {x.comune}
          </button>
        ))}
      </div>
      <div className="flex flex-wrap gap-2">
        <Button onClick={save} data-testid="alloggiati-save-button">Salva</Button>
        <Button variant="outline" onClick={test} data-testid="alloggiati-test-button"><PlugZap className="h-4 w-4 mr-2" />Test connessione</Button>
      </div>
      {res && <p className={`rounded-lg p-3 text-sm ${res.ok ? "bg-emerald-50 text-emerald-800" : "bg-rose-50 text-rose-800"}`} data-testid="alloggiati-test-result">{res.message}</p>}
    </Card>
  );
}

const MEZZI_A = ["AEREOCOMPLINEA", "AEREOCHARTLOW", "TRAGHETTO", "NAVEPRIV", "CROCIERA", "TRENO", "BUS", "AUTO", "ROULOTTE", "CAMPER", "BICI", "MOTO"];
const MEZZI_M = ["TRAGHETTO", "NAVEPRIV", "CROCIERA", "TRENO", "BUS", "AUTO", "ROULOTTE", "CAMPER", "BICI", "MOTO"];
const MOTIVI = ["BALNEARE", "RELIGIOSO", "SPORTIVOBENESSERE", "ARTECULTURAEVENTI", "NATURA", "ENOGASTRONOMIA", "AFFARICONGRESSI", "VISITAPARENTI", "PERSONALE"];
const CARATT = ["DIRETTAALLOGGIOINTERNET", "DIRETTAALLOGGIOSR", "MEZZOTRASPORTOARRIVOINTERNET", "MEZZOTRASPORTOMOVIMENTOINTERNET", "AGENZIA", "TOUR", "ASSOCIAZIONE"];

export function SpotTab({ prop, onSaved }) {
  const s = prop.spot || {};
  const [f, setF] = useState({ vendor: "BnB Checkin Puglia", vendor_on: "root", client_code_tag: "codiceclientesr", mezzotrasportoarrivo: "AUTO", mezzotrasportomovimento: "AUTO", motivazioniviaggio: "BALNEARE", caratteristica: "DIRETTAALLOGGIOINTERNET", send_duratasoggiorno: true, ...s });
  const set = (k) => (v) => setF({ ...f, [k]: v?.target ? v.target.value : v });
  const save = async () => {
    try {
      await api.put("/settings/spot", f);
      toast.success("Parametri SPOT salvati");
      onSaved();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <Card title="Parametri file DMS / SPOT Online" desc="Schema movimentogiornaliero-0.6. I campi obbligatori non raccolti dal check-in (mezzo di trasporto, motivazione) usano questi valori predefiniti: verificali con le specifiche ufficiali.">
      <div className="grid gap-4 sm:grid-cols-2">
        <F label="Nome software (attributo vendor)"><Input value={f.vendor} onChange={set("vendor")} data-testid="spot-vendor-input" /></F>
        <F label="Posizione attributo vendor"><Sel value={f.vendor_on} onChange={set("vendor_on")} options={["root", "movimento"]} testId="spot-vendor-on-select" /></F>
        <F label="Tag codice cliente"><Sel value={f.client_code_tag} onChange={set("client_code_tag")} options={["codiceclientesr", "codicecliente"]} testId="spot-client-tag-select" /></F>
        <F label="Mezzo di trasporto arrivo (predefinito)"><Sel value={f.mezzotrasportoarrivo} onChange={set("mezzotrasportoarrivo")} options={MEZZI_A} testId="spot-mezzo-arrivo-select" /></F>
        <F label="Mezzo di trasporto in Puglia (predefinito)"><Sel value={f.mezzotrasportomovimento} onChange={set("mezzotrasportomovimento")} options={MEZZI_M} testId="spot-mezzo-mov-select" /></F>
        <F label="Motivazione viaggio (predefinita)"><Sel value={f.motivazioniviaggio} onChange={set("motivazioniviaggio")} options={MOTIVI} testId="spot-motivo-select" /></F>
        <F label="Caratteristica prenotazione"><Sel value={f.caratteristica} onChange={set("caratteristica")} options={CARATT} testId="spot-caratt-select" /></F>
        <label className="flex items-center justify-between rounded-xl border p-4 text-sm">Includi durata soggiorno<Switch checked={f.send_duratasoggiorno} onCheckedChange={set("send_duratasoggiorno")} data-testid="spot-durata-switch" /></label>
      </div>
      <Button onClick={save} data-testid="spot-save-button">Salva</Button>
    </Card>
  );
}

export function TablesTab({ prop, onSaved }) {
  const [busy, setBusy] = useState(false);
  const refresh = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/settings/tabelle/refresh");
      toast.success(`Tabelle aggiornate: ${data.luoghi} luoghi, ${data.documenti} documenti`);
      onSaved();
    } catch (e) {
      toast.error(errMsg(e));
    }
    setBusy(false);
  };
  const delDemo = async () => {
    if (!window.confirm("Eliminare tutte le prenotazioni di esempio?")) return;
    const { data } = await api.delete("/settings/demo-data");
    toast.success(`Eliminate ${data.deleted} prenotazioni di esempio`);
  };
  return (
    <Card title="Tabelle codici Alloggiati Web" desc="Comuni, stati e tipi documento usati per l'autocompletamento, scaricati dal web service ufficiale (richiede modalità reale e credenziali).">
      <p className="text-sm" data-testid="tables-status">{prop.tables_sample ? "Attive: tabelle DI ESEMPIO (poche voci, codici da verificare)." : `Ultimo aggiornamento: ${prop.tables_updated_at ? new Date(prop.tables_updated_at).toLocaleString("it-IT") : "-"}`}</p>
      <div className="flex flex-wrap gap-2">
        <Button onClick={refresh} disabled={busy} data-testid="refresh-tables-button"><RefreshCw className={`h-4 w-4 mr-2 ${busy ? "animate-spin" : ""}`} />Scarica tabelle ufficiali</Button>
        <Button variant="ghost" className="text-rose-700" onClick={delDemo} data-testid="delete-demo-data-button"><Trash2 className="h-4 w-4 mr-2" />Elimina dati di esempio</Button>
      </div>
    </Card>
  );
}

export function PrivacyTab({ prop, onSaved }) {
  const [f, setF] = useState({ enabled: true, days_after_receipt: 30, mode: "anonymize", delete_photos_after_confirm: true, ...(prop.retention || {}) });
  const save = async () => {
    try {
      await api.put("/settings/retention", { ...f, days_after_receipt: Number(f.days_after_receipt) });
      toast.success("Policy di conservazione salvata");
      onSaved();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const run = async () => {
    await api.post("/jobs/run-now");
    toast.success("Job pianificati eseguiti");
  };
  return (
    <Card title="Conservazione dei dati (retention)" desc="Dopo la ricevuta Alloggiati i dati personali vengono anonimizzati: restano solo sesso, età, cittadinanza, residenza e date per la movimentazione ISTAT.">
      <div className="grid gap-4 sm:grid-cols-2">
        <label className="flex items-center justify-between rounded-xl border p-4 text-sm">Anonimizzazione automatica<Switch checked={f.enabled} onCheckedChange={(v) => setF({ ...f, enabled: v })} data-testid="retention-enabled-switch" /></label>
        <F label="Giorni dopo la ricevuta"><Input type="number" min={0} value={f.days_after_receipt} onChange={(e) => setF({ ...f, days_after_receipt: e.target.value })} data-testid="retention-days-input" /></F>
        <label className="flex items-center justify-between rounded-xl border p-4 text-sm">Elimina foto documento alla conferma de visu<Switch checked={f.delete_photos_after_confirm} onCheckedChange={(v) => setF({ ...f, delete_photos_after_confirm: v })} data-testid="retention-photos-switch" /></label>
      </div>
      <div className="flex gap-2">
        <Button onClick={save} data-testid="retention-save-button">Salva</Button>
        <Button variant="outline" onClick={run} data-testid="run-jobs-button">Esegui ora i job pianificati</Button>
      </div>
    </Card>
  );
}
